<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useBriefStore } from "@/stores/briefs";
import TaskDrawer from "@/components/TaskDrawer.vue";
import AssetPreviewModal from "@/components/AssetPreviewModal.vue";
import { api } from "@/services/api";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileImage,
  LoaderCircle,
  RefreshCw,
  Search,
  Square,
  X,
} from "lucide-vue-next";
import type { V2Asset } from "@/types";
import { v2 } from "@/services/v2";
import { v3 } from "@/services/v3";
import { useProjectStore } from "@/stores/projects";
import SolutionReviewPanel from "@/modules/solutions/SolutionReviewPanel.vue";
import ProjectDraftEditor from "@/modules/projects/ProjectDraftEditor.vue";
import ClientProjectView from "@/modules/projects/ClientProjectView.vue";

const props = defineProps<{
  showHistory?: boolean;
  briefId?: number;
  startNew?: boolean;
}>();
const emit = defineEmits<{ openBrief: [id: number] }>();
const store = useBriefStore();
const projectStore = useProjectStore();
const { t, locale } = useI18n();
const input = ref("");
const changeInput = ref("");
const changeOpen = ref(false);
const messagesPane = ref<HTMLElement | null>(null);
const editingTitle = ref(false);
const titleInput = ref("");
const pendingAssets = ref<V2Asset[]>([]);
const uploadingReference = ref(false);
const confirmingSolution = ref(false);
const mobilePane = ref<"conversation" | "solution">("conversation");
const pageLoading = ref(false);
const pageError = ref("");
const sendError = ref("");
const previewAsset = ref<V2Asset | null>(null);
const thinkingExpanded = ref(false);
const followLatestMessage = ref(true);
const availableSkills = ref<
  Array<{ id: string; name: string; description: string }>
>([]);
const active = computed(() => store.active);
const selectedSkillName = computed(
  () =>
    availableSkills.value.find((skill) => skill.id === active.value?.skill_id)
      ?.name || "通用需求",
);
const clientText = (task: {
  client_title?: string;
  title: string;
  client_scope?: string;
  scope: string;
}) => ({
  title: task.client_title || task.title,
  scope: task.client_scope || task.scope,
});
let refreshTimer: number | undefined;
let loadSequence = 0;
const intro = computed(() =>
  locale.value.startsWith("fr")
    ? "Decrivez votre besoin dans votre propre langue, puis confirmez la liste des taches."
    : locale.value.startsWith("en")
      ? "Describe the requirement in your own language and confirm the task list before submission."
      : "像聊天一样补充需求，确认后再提交给团队。",
);
const inputPlaceholder = computed(() =>
  locale.value.startsWith("fr")
    ? "Ecrivez votre besoin dans votre propre langue…"
    : locale.value.startsWith("en")
      ? "Write your requirement in your own language…"
      : "输入中文、英文、法语或混合语言的需求内容…",
);
const statusText = (status: string) => {
  const labels = locale.value.startsWith("fr")
    ? {
        draft: "A confirmer",
        submitted: "Recu",
        in_progress: "En cours",
        ready_for_acceptance: "Pret a valider",
        accepted: "Accepte",
        revision_requested: "Modification demandee",
      }
    : locale.value.startsWith("en")
      ? {
          draft: "Awaiting confirmation",
          submitted: "Received",
          in_progress: "In progress",
          ready_for_acceptance: "Ready for review",
          accepted: "Accepted",
          revision_requested: "Revision requested",
        }
      : {
          draft: "待确认",
          submitted: "已接收",
          in_progress: "制作中",
          ready_for_acceptance: "待客户验收",
          accepted: "已验收",
          revision_requested: "客户要求修改",
        };
  return labels[status as keyof typeof labels] || status;
};
async function loadActiveBrief() {
  const sequence = ++loadSequence;
  pageLoading.value = true;
  pageError.value = "";
  sendError.value = "";
  mobilePane.value = "conversation";
  store.active = null;
  projectStore.clearBriefContext();
  try {
    await store.loadList();
    if (props.startNew) await store.create();
    else if (props.briefId) await store.open(props.briefId);
    else if (store.briefs[0]) await store.open(store.briefs[0].id);
    else await store.create();
    if (store.active) await syncV2();
    if (active.value?.status === "draft") {
      void store.resumeAgentRun().catch((error) => {
        sendError.value = error instanceof Error ? error.message : "任务恢复失败";
      });
    }
  } catch (error) {
    if (sequence === loadSequence) {
      pageError.value =
        error instanceof Error ? error.message : "页面加载失败，请重试";
    }
  } finally {
    if (sequence === loadSequence) pageLoading.value = false;
  }
}
async function syncV2() {
  if (!store.active) return;
  const brief = store.active;
  try {
    await projectStore.loadSolution(brief.id);
  } catch (error) {
    if (
      brief.lifecycle_state === "project_draft" ||
      brief.status !== "draft"
    ) {
      throw error;
    }
  }
  if (
    brief.lifecycle_state === "project_draft" ||
    brief.status !== "draft"
  ) {
    await projectStore.loadDraft(brief.id);
  }
}
onMounted(async () => {
  try {
    availableSkills.value = (
      await api<{
        skills: Array<{ id: string; name: string; description: string }>;
      }>("/skills")
    ).skills;
  } catch {
    availableSkills.value = [];
  }
  await loadActiveBrief();
  refreshTimer = window.setInterval(() => {
    void store.refresh().catch(() => undefined);
  }, 8000);
});
watch(
  () => [props.briefId, props.startNew],
  async () => {
    if (!props.showHistory) await loadActiveBrief();
  },
);
function isMessagesPaneNearBottom() {
  const pane = messagesPane.value;
  if (!pane) return true;
  return pane.scrollHeight - pane.scrollTop - pane.clientHeight <= 80;
}
function handleMessagesScroll() {
  followLatestMessage.value = isMessagesPaneNearBottom();
}
async function scrollMessagesToBottom(force = false) {
  await nextTick();
  if (!messagesPane.value || (!force && !followLatestMessage.value)) return;
  messagesPane.value.scrollTop = messagesPane.value.scrollHeight;
  followLatestMessage.value = true;
}
watch(
  () => active.value?.messages.length,
  () => {
    void scrollMessagesToBottom();
  },
);
watch(
  () => active.value?.id,
  () => {
    followLatestMessage.value = true;
    void scrollMessagesToBottom(true);
  },
);
watch(
  () => [
    store.streamedReply.length,
    store.agentThinking.length,
    store.agentSources.length,
    store.agentSteps.length,
    store.agentProgress,
  ],
  () => {
    void scrollMessagesToBottom();
  },
);
watch(
  () => store.loading,
  (loading) => {
    thinkingExpanded.value = loading;
  },
);
onUnmounted(() => {
  if (refreshTimer) window.clearInterval(refreshTimer);
});
async function send() {
  const typedContent = input.value.trim();
  const content =
    typedContent ||
    (pendingAssets.value.length
      ? locale.value.startsWith("zh")
        ? "请结合我上传的参考图片继续整理需求。"
        : "Please continue organizing the requirement using the attached reference image."
      : "");
  if (!content) return;
  const previousMessageId = Math.max(
    0,
    ...(active.value?.messages.map((message) => message.id) || []),
  );
  const sendingAssets = [...pendingAssets.value];
  sendError.value = "";
  input.value = "";
  followLatestMessage.value = true;
  try {
    await store.sendRequirement(content, sendingAssets.map((asset) => asset.id));
    pendingAssets.value = [];
    await syncV2();
    if (projectStore.solution?.readiness.readyForClientReview) {
      mobilePane.value = "solution";
    }
  } catch (error) {
    const persisted = active.value?.messages.some(
      (message) =>
        message.id > previousMessageId &&
        message.role === "user" &&
        message.content === content,
    );
    if (persisted) pendingAssets.value = [];
    else input.value = typedContent;
    sendError.value =
      error instanceof Error
        ? error.message
        : "发送失败，请检查网络后重试";
  }
}
async function uploadConversationAsset(event: Event) {
  const element = event.target as HTMLInputElement;
  const file = element.files?.[0];
  if (!file || !active.value) return;
  uploadingReference.value = true;
  sendError.value = "";
  try {
    if (file.type === "text/plain" || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
      const result = await v3.uploadDocument(active.value.id, file);
      input.value = `${input.value}${input.value ? "\n\n" : ""}请解析附件《${result.fileName}》并据此生成完整方案和两级任务清单。\n\n${result.extractedText}`.slice(0, 100000);
      return;
    }
    const result = await v2.uploadConversationAsset(active.value.id, file);
    pendingAssets.value.push(result.asset);
  } catch (error) {
    sendError.value =
      error instanceof Error ? error.message : "文件上传失败，请重试";
  } finally {
    uploadingReference.value = false;
    element.value = "";
  }
}
async function confirmSolution() {
  if (!active.value) return;
  confirmingSolution.value = true;
  try {
    await projectStore.confirmCurrentSolution(active.value.id);
    await store.refresh();
  } finally {
    confirmingSolution.value = false;
  }
}
async function addChange() {
  if (!changeInput.value.trim()) return;
  await store.createChange(changeInput.value);
  changeInput.value = "";
  changeOpen.value = false;
}
async function refreshTask() {
  if (!store.activeTask) return;
  await store.openTask(store.activeTask.id);
  await store.refresh();
}
async function openBrief(id: number) {
  await store.open(id);
  emit("openBrief", id);
}
function editTitle() {
  titleInput.value = active.value?.title || "";
  editingTitle.value = true;
}
async function saveTitle() {
  if (!titleInput.value.trim()) return;
  await store.renameActive(titleInput.value.trim());
  editingTitle.value = false;
}
async function changeSkill(skillId: string) {
  if (!active.value) return;
  store.active = (
    await api<{ brief: any }>(`/briefs/${active.value.id}/skill`, {
      method: "PATCH",
      body: JSON.stringify({ skillId }),
    })
  ).brief;
  await store.loadList();
}
function assistantText(content: string) {
  let value: unknown = content;
  for (let depth = 0; depth < 3; depth += 1) {
    if (typeof value !== "string") break;
    try {
      const parsed = JSON.parse(value);
      value = locale.value.startsWith("zh")
        ? parsed.reply_client || parsed.summary_zh || parsed.summary_client
        : parsed.reply_client || parsed.summary_client || parsed.summary_zh;
    } catch {
      break;
    }
  }
  return typeof value === "string" ? value : content;
}
function assistantQuestions(content: string): string[] {
  try {
    const questions = JSON.parse(content).open_questions;
    return Array.isArray(questions) ? questions : [];
  } catch {
    return [];
  }
}
function formatMessage(content: string) {
  const escape = (value: string) =>
    value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escape(assistantText(content))
    .replace(/^#{1,3}\s+(.+)$/gm, '<strong class="agent-heading">$1</strong>')
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`(.+?)`/g, "<code>$1</code>")
    .replace(/\n/g, "<br>");
}
</script>

<template>
  <div v-if="pageLoading" class="page-state" aria-live="polite">
    <span class="agent-live__dot"></span><b>正在加载...</b>
  </div>
  <div v-else-if="pageError" class="page-state page-state--error">
    <b>页面暂时没有加载成功</b>
    <p>{{ pageError }}</p>
    <button class="btn btn--primary" @click="loadActiveBrief">
      <RefreshCw :size="16" />重新加载
    </button>
  </div>
  <div v-else-if="showHistory" class="history-page">
    <header class="page-head">
      <div>
        <h1>{{ t("myBriefs") }}</h1>
        <p class="muted">
          {{
            locale.startsWith("zh")
              ? "每一份 Brief 都保留独立会话、任务、变更和交付记录。"
              : "Every Brief keeps its own conversation, tasks, changes, and deliveries."
          }}
        </p>
      </div>
      <button class="btn btn--primary" @click="store.create()">
        {{ t("newBrief") }}
      </button>
    </header>
    <div class="history-list">
      <button
        v-for="brief in store.briefs"
        :key="brief.id"
        class="history-card"
        @click="openBrief(brief.id)"
      >
        <span
          ><b>{{ brief.title }}</b
          ><small>{{ statusText(brief.status) }}</small></span
        ><span class="muted">{{
          brief.updated_at.slice(0, 16).replace("T", " ")
        }}</span>
      </button>
      <p v-if="!store.briefs.length" class="muted">
        {{ locale.startsWith("zh") ? "还没有 Brief。" : "No Briefs yet." }}
      </p>
    </div>
  </div>
  <ProjectDraftEditor
    v-else-if="
      active &&
      active.lifecycle_state === 'project_draft' &&
      projectStore.active
      && projectStore.active.id === active.id
    "
    :project="projectStore.active"
    @changed="store.refresh()"
    @submitted="store.refresh()"
  />
  <ClientProjectView
    v-else-if="
      active &&
      active.status !== 'draft' &&
      projectStore.active &&
      projectStore.active.id === active.id
    "
    :project="projectStore.active"
    @open-task="store.openTask"
    @add-change="changeOpen = true"
    @changed="syncV2"
  />
  <div v-else-if="active" class="client-page">
    <header class="page-head">
      <div>
        <div v-if="editingTitle" class="title-edit">
          <input
            v-model="titleInput"
            class="input"
            maxlength="120"
            @keyup.enter="saveTitle"
          /><button class="btn btn--primary" @click="saveTitle">保存</button
          ><button class="btn" @click="editingTitle = false">取消</button>
        </div>
        <h1 v-else>
          {{ active.title }}
          <button
            class="title-edit__button"
            title="修改 Brief 标题"
            @click="editTitle"
          >
            编辑
          </button>
        </h1>
        <p class="muted">{{ intro }}</p>
        <div
          v-if="active.status === 'draft' && availableSkills.length"
          class="skill-routing"
        >
          <span :class="{ warning: active.skill_confirmation_required }"
            >{{
              active.skill_confirmation_required
                ? "请确认业务类型"
                : "已自动识别"
            }}：<b>{{ selectedSkillName }}</b></span
          ><select
            class="skill-picker"
            :value="active.skill_id"
            aria-label="更改业务类型"
            @change="changeSkill(($event.target as HTMLSelectElement).value)"
          >
            <option value="brief-confirmation" disabled>选择业务类型</option>
            <option
              v-for="skill in availableSkills"
              :key="skill.id"
              :value="skill.id"
            >
              {{ skill.name }}
            </option>
          </select>
        </div>
        <small v-if="active.versions?.length" class="version-label"
          >已确认 Brief v{{ active.versions[0].version_number }}</small
        >
      </div>
      <button class="btn btn--primary" @click="store.create()">
        {{ t("newBrief") }}
      </button>
    </header>
    <nav class="mobile-panes" aria-label="需求页面视图">
      <button :class="{ active: mobilePane === 'conversation' }" @click="mobilePane = 'conversation'">需求沟通</button>
      <button :class="{ active: mobilePane === 'solution' }" @click="mobilePane = 'solution'">完整方案</button>
    </nav>
    <div class="client-layout">
      <section class="conversation" :class="{ 'mobile-hidden': mobilePane !== 'conversation' }">
        <div ref="messagesPane" class="messages" @scroll.passive="handleMessagesScroll">
          <article
            v-for="message in active.messages"
            :key="message.id"
            class="bubble"
            :class="{ mine: message.role === 'user' }"
          >
            <small>{{
              message.role === "assistant" ? "Brief Agent" : "You"
            }}</small
            ><template v-if="message.role === 'assistant'"
              ><div
                class="agent-copy"
                v-html="formatMessage(message.content)"
              ></div>
              <ul
                v-if="assistantQuestions(message.content).length"
                class="questions"
              >
                <li
                  v-for="question in assistantQuestions(message.content)"
                  :key="question"
                >
                  {{ question }}
                </li>
              </ul></template
            ><template v-else>{{ message.content }}</template>
            <div v-if="message.assets?.length" class="message-assets">
              <button
                v-for="asset in message.assets"
                :key="asset.id"
                type="button"
                @click="previewAsset = asset"
              >
                <FileImage :size="18" />
                <span>{{ asset.originalName }}</span>
              </button>
            </div>
          </article>
          <section
            v-if="store.loading || store.agentThinking || store.agentSources.length"
            class="agent-live"
            :class="{ complete: store.agentTraceComplete && !store.loading }"
          >
            <header>
              <span class="agent-live__state">
                <LoaderCircle v-if="store.loading" :size="17" class="spin" />
                <CheckCircle2 v-else :size="17" />
                <b>{{ store.agentProgress || "研究与规划已完成" }}</b>
              </span>
              <span class="agent-live__actions">
                <button v-if="store.loading" class="icon-btn" title="停止当前任务" @click="store.cancelAgentRun"><Square :size="15" /></button>
                <button class="icon-btn" :title="thinkingExpanded ? '折叠过程' : '展开过程'" @click="thinkingExpanded = !thinkingExpanded">
                  <ChevronUp v-if="thinkingExpanded" :size="17" /><ChevronDown v-else :size="17" />
                </button>
              </span>
            </header>
            <div v-if="thinkingExpanded" class="agent-trace">
              <ol v-if="store.agentSteps.length" class="agent-steps">
                <li v-for="step in store.agentSteps" :key="step.key" :class="step.status">
                  <CheckCircle2 v-if="step.status === 'done'" :size="14" /><LoaderCircle v-else :size="14" class="spin" />
                  <span>{{ step.label }}</span>
                </li>
              </ol>
              <div v-if="store.agentThinking" class="thinking-copy">{{ store.agentThinking }}</div>
              <section v-if="store.agentSources.length" class="research-sources">
                <h4><Search :size="14" />参考资料</h4>
                <a v-for="source in store.agentSources" :key="source.url" :href="source.url" target="_blank" rel="noopener noreferrer">
                  <b>{{ source.title }}</b><small>{{ source.url }}</small>
                </a>
              </section>
            </div>
            <p v-if="store.streamedReply" class="streamed-answer">{{ store.streamedReply }}</p>
          </section>
        </div>
        <div class="composer">
          <p v-if="sendError" class="send-error" role="alert">{{ sendError }}</p>
          <div v-if="pendingAssets.length" class="pending-assets">
            <span v-for="asset in pendingAssets" :key="asset.id"
              >{{ asset.originalName
              }}<button
                title="移除本次引用"
                @click="pendingAssets = pendingAssets.filter((item) => item.id !== asset.id)"
              ><X :size="14" /></button></span
            >
          </div>
          <textarea
            v-model="input"
            class="textarea"
            :placeholder="inputPlaceholder"
          />
          <label class="reference-upload" title="上传图片、视频、Word 或 TXT">
            <FileImage :size="18" />
            <input type="file" accept="image/*,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,.docx,.txt" :disabled="uploadingReference" @change="uploadConversationAsset" />
          </label>
          <button
            class="btn btn--primary"
            :disabled="store.loading"
            @click="send"
          >
            {{ t("send") }}
          </button>
        </div>
      </section>
      <aside class="task-panel" :class="{ 'mobile-hidden': mobilePane !== 'solution' }">
        <SolutionReviewPanel
          v-if="active.status === 'draft'"
          :solution="projectStore.solution"
          :confirming="confirmingSolution"
          @confirm="confirmSolution"
        />
        <template v-else>
        <h2>{{ t("taskList") }}</h2>
        <div class="summary">
          <span>{{ statusText(active.status) }}</span
          ><b
            >{{ active.tasks.filter((task) => task.confirmed).length }} /
            {{ active.tasks.length }}</b
          >
        </div>
        <article v-for="task in active.tasks" :key="task.id" class="task-card">
          <label
            ><input
              type="checkbox"
              :checked="Boolean(task.confirmed)"
              :disabled="active.status !== 'draft'"
              @change="
                store.confirmTask(
                  task.id,
                  ($event.target as HTMLInputElement).checked,
                )
              "
            />
            <div>
              <b>{{ clientText(task).title }}</b>
              <p>{{ clientText(task).scope }}</p>
              <span
                v-for="item in task.client_deliverables?.length
                  ? task.client_deliverables
                  : task.deliverables"
                :key="item"
                class="chip"
                >{{ item }}</span
              >
              <ul
                v-if="
                  (task.client_acceptance_criteria?.length
                    ? task.client_acceptance_criteria
                    : task.acceptance_criteria
                  )?.length
                "
                class="acceptance"
              >
                <li
                  v-for="criterion in task.client_acceptance_criteria?.length
                    ? task.client_acceptance_criteria
                    : task.acceptance_criteria"
                  :key="criterion"
                >
                  {{ criterion }}
                </li>
              </ul>
            </div></label
          >
          <div class="task-card__foot">
            <span class="status">{{ statusText(task.status) }}</span
            ><button
              v-if="task.confirmed && active.status !== 'draft'"
              class="btn"
              @click="store.openTask(task.id)"
            >
              {{ locale.startsWith("zh") ? "沟通与交付" : "Messages" }}
            </button>
          </div>
        </article>
        <section
          v-if="active.versions?.length || active.change_requests?.length"
          class="brief-records"
        >
          <h3>版本与变更</h3>
          <div v-for="version in active.versions" :key="version.id">
            <b>Brief v{{ version.version_number }}</b
            ><span>{{
              version.state === "confirmed" ? "已确认" : version.state
            }}</span>
          </div>
          <div v-for="change in active.change_requests" :key="change.id">
            <b>变更 #{{ change.id }}</b
            ><span>{{
              change.team_status === "new" ? "等待团队确认" : change.team_status
            }}</span>
          </div>
        </section>
        <button
          v-if="active.status === 'draft' && active.tasks.length"
          class="btn btn--primary full"
          @click="store.submit"
        >
          {{ t("submit") }}</button
        ><button
          v-else-if="active.status !== 'draft'"
          class="btn full"
          @click="changeOpen = true"
        >
          {{ t("addChange") }}
        </button>
        </template>
      </aside>
    </div>
  </div>
  <Teleport to="body"
      ><div v-if="active && changeOpen" class="modal">
        <form class="modal__panel" @submit.prevent="addChange">
          <h2>{{ t("addChange") }}</h2>
          <p class="muted">
            {{
              locale.startsWith("zh")
                ? "团队将收到 AI 整理后的中文执行说明。"
                : locale.startsWith("fr")
                  ? "L equipe recevra un resume d execution structure en chinois."
                  : "The team receives a structured Chinese execution summary."
            }}
          </p>
          <textarea v-model="changeInput" class="textarea" required />
          <div class="modal__actions">
            <button type="button" class="btn" @click="changeOpen = false">
              取消</button
            ><button class="btn btn--primary">提交</button>
          </div>
        </form>
      </div></Teleport>
    <TaskDrawer
      :task="store.activeTask"
      @close="store.activeTask = null"
      @changed="refreshTask"
    />
    <AssetPreviewModal :asset="previewAsset" @close="previewAsset = null" />
</template>

<style scoped>
.agent-live {
  display: grid;
  gap: 10px;
  margin: 0 0 14px;
  padding: 12px 14px;
  border: 1px solid #cfe3ef;
  background: #f3f9fc;
}
.agent-live.complete{border-color:#cfe4d9;background:#f5faf7}.agent-live>header{display:flex;align-items:center;justify-content:space-between;gap:12px}.agent-live__state,.agent-live__actions{display:flex;align-items:center;gap:8px}.agent-live__state>svg{color:#2c87bb}.agent-live.complete .agent-live__state>svg{color:var(--success)}.agent-live__actions .icon-btn{display:grid;width:34px;height:34px;place-items:center;border:1px solid var(--line);background:#fff}.agent-trace{display:grid;gap:10px;padding-top:10px;border-top:1px solid rgba(47,100,127,.16)}.agent-steps{display:flex;flex-wrap:wrap;gap:7px;margin:0;padding:0;list-style:none}.agent-steps li{display:flex;align-items:center;gap:5px;padding:4px 7px;background:#fff;color:var(--muted);font-size:11px}.agent-steps li.active{color:#2f647f}.agent-steps li.done svg{color:var(--success)}.thinking-copy{max-height:210px;overflow:auto;white-space:pre-wrap;padding:10px;border-left:3px solid #7aa9bf;background:#fff;color:#42565f;font-size:12px;line-height:1.65}.research-sources{display:grid;gap:5px}.research-sources h4{display:flex;align-items:center;gap:5px;margin:0;font-size:12px}.research-sources a{display:grid;gap:2px;min-width:0;padding:7px 8px;border:1px solid var(--line);background:#fff;color:var(--ink);text-decoration:none}.research-sources a small{overflow:hidden;color:var(--muted);font-size:10px;text-overflow:ellipsis;white-space:nowrap}.streamed-answer{margin:0!important;white-space:pre-wrap;line-height:1.7}.spin{animation:spin .9s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}
.agent-live__dot {
  width: 8px;
  height: 8px;
  margin-top: 5px;
  border-radius: 50%;
  background: #2c87bb;
  box-shadow: 0 0 0 0 rgba(44, 135, 187, 0.35);
  animation: pulse 1.25s infinite;
}
.agent-live p {
  margin: 3px 0 0;
  white-space: pre-wrap;
  line-height: 1.65;
}
.agent-live b {
  font-size: 13px;
  color: #2f647f;
}
.page-state {
  min-height: calc(100dvh - 64px);
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 10px;
  padding: 24px;
  background: #f5f7f7;
  text-align: center;
}
.page-state--error p {
  max-width: 520px;
  margin: 0;
  color: var(--muted);
}
.page-state .btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}
@keyframes pulse {
  70% {
    box-shadow: 0 0 0 7px rgba(44, 135, 187, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(44, 135, 187, 0);
  }
}
.skill-routing {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 9px;
}
.skill-routing > span {
  font-size: 12px;
  color: var(--success);
}
.skill-routing > span.warning {
  color: #b56a24;
}
.skill-picker {
  border: 1px solid var(--line);
  padding: 6px 8px;
  background: #fff;
}
.version-label {
  display: inline-block;
  margin-top: 8px;
  color: var(--muted);
}
.title-edit {
  display: flex;
  align-items: center;
  gap: 7px;
}
.title-edit .input {
  min-width: 260px;
}
.title-edit__button {
  margin-left: 8px;
  border: 0;
  background: transparent;
  color: var(--brand-dark);
  font-size: 12px;
  font-weight: 400;
}
.agent-copy {
  line-height: 1.72;
  word-break: break-word;
}
.agent-copy :deep(.agent-heading) {
  display: block;
  margin: 10px 0 4px;
  color: var(--ink);
  font-size: 15px;
}
.agent-copy :deep(code) {
  padding: 1px 4px;
  background: #f3f5f5;
  color: #48545a;
}
.agent-copy :deep(strong) {
  font-weight: 700;
}
.questions {
  margin: 9px 0 0;
  padding-left: 18px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
}
.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 26px 30px 18px;
  background: #fff;
  border-bottom: 1px solid var(--line);
}
.page-head h1 {
  margin: 0;
  font-size: 22px;
}
.page-head p {
  margin: 5px 0 0;
}
.client-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
}
.mobile-panes{display:none}
.conversation {
  display: flex;
  min-height: calc(100vh - 160px);
  flex-direction: column;
}
.messages {
  flex: 1;
  padding: 26px 30px;
}
.bubble {
  max-width: 760px;
  margin: 0 0 14px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  background: #fff;
}
.bubble.mine {
  margin-left: auto;
  background: #fff0e7;
  border-color: #f2c7a8;
}
.bubble small {
  display: block;
  margin-bottom: 4px;
  color: var(--muted);
}
.message-assets {
  display: grid;
  gap: 6px;
  margin-top: 9px;
}
.message-assets button {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  align-items: center;
  gap: 7px;
  width: min(300px, 100%);
  border: 1px solid rgba(27, 42, 51, 0.16);
  padding: 8px 9px;
  background: rgba(255, 255, 255, 0.72);
  color: var(--ink);
  text-align: left;
}
.message-assets span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.composer {
  position: sticky;
  bottom: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 40px auto;
  gap: 10px;
  padding: 14px 30px;
  background: #fff;
  border-top: 1px solid var(--line);
}
.pending-assets{grid-column:1/-1;display:flex;flex-wrap:wrap;gap:6px}.pending-assets span{display:flex;align-items:center;gap:5px;max-width:100%;padding:5px 7px;background:#eef4f6;font-size:12px}.pending-assets button{display:grid;width:20px;height:20px;place-items:center;border:0;background:transparent;color:var(--muted)}.reference-upload{display:grid;width:40px;height:40px;place-items:center;align-self:end;border:1px solid var(--line);background:#fff;color:var(--ink);cursor:pointer}.reference-upload input{display:none}
.send-error{grid-column:1/-1;margin:0;padding:8px 10px;background:#fff2f0;color:var(--danger);font-size:12px}
.task-panel {
  padding: 22px 18px;
  border-left: 1px solid var(--line);
  background: #fbfcfc;
}
.task-panel h2 {
  margin: 0 0 12px;
  font-size: 16px;
}
.brief-records {
  display: grid;
  gap: 6px;
  margin-top: 14px;
  padding-top: 13px;
  border-top: 1px solid var(--line);
}
.brief-records h3 {
  margin: 0 0 3px;
  font-size: 14px;
}
.brief-records div {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
}
.brief-records span {
  color: var(--muted);
}
.summary {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
}
.task-card {
  margin-top: 10px;
  padding: 11px;
  border: 1px solid var(--line);
  background: #fff;
}
.task-card label {
  display: flex;
  gap: 9px;
}
.task-card p {
  margin: 4px 0;
  color: var(--muted);
  font-size: 13px;
}
.task-card__foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 9px;
}
.chip {
  display: inline-block;
  margin: 3px 4px 0 0;
  padding: 2px 5px;
  background: #f4f6f7;
  color: var(--muted);
  font-size: 12px;
}
.acceptance {
  margin: 8px 0 0;
  padding-left: 17px;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.5;
}
.full {
  width: 100%;
  margin-top: 13px;
}
.modal {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: grid;
  place-items: center;
  background: rgba(20, 30, 40, 0.35);
  padding: 20px;
}
.modal__panel {
  width: min(500px, 100%);
  display: grid;
  gap: 12px;
  padding: 22px;
  background: #fff;
}
.modal__panel h2 {
  margin: 0;
}
.modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
@media (max-width: 700px) {
  .page-state { min-height: calc(100dvh - 56px - 62px - env(safe-area-inset-bottom)); }
  .page-head,
  .messages,
  .composer {
    padding-left: 14px;
    padding-right: 14px;
  }
  .client-layout {
    grid-template-columns: 1fr;
  }
  .task-panel {
    border-left: 0;
    border-bottom:0;
  }
  .conversation {
    min-height: 0;
  }
  .composer {
    position:sticky;
    z-index:5;
    bottom:0;
    padding: 9px 12px;
  }
  .composer .textarea {
    min-height: 46px;
  }
  .messages { padding-bottom:14px; }
}
.history-page {
  min-height: calc(100vh - 64px);
}
.history-list {
  max-width: 960px;
  display: grid;
  gap: 10px;
  padding: 24px 30px;
}
.history-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 15px;
  border: 1px solid var(--line);
  background: #fff;
  text-align: left;
}
.history-card:hover {
  border-color: #efb98e;
  background: #fff9f4;
}
.history-card b,
.history-card small {
  display: block;
}
.history-card small {
  margin-top: 3px;
  color: var(--muted);
}
.client-page {
  display: flex;
  height: calc(100dvh - 64px);
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
}
.client-layout {
  flex: 1;
  min-height: 0;
}
.conversation {
  height: 100%;
  min-height: 0;
}
.messages {
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
  overflow-anchor: none;
}
.task-panel {
  min-height: 0;
  overflow-y: auto;
}
@media (max-width: 700px) {
  .client-page {
    height: calc(100dvh - 56px - 62px - env(safe-area-inset-bottom));
  }
  .client-layout {
    overflow: hidden;
  }
  .task-panel {
    width:100%;
    max-height:none;
    overflow-y: auto;
  }
  .mobile-panes{display:grid;grid-template-columns:1fr 1fr;flex:0 0 auto;border-bottom:1px solid var(--line);background:#fff}.mobile-panes button{min-height:42px;border:0;border-bottom:2px solid transparent;background:#fff;color:var(--muted)}.mobile-panes button.active{border-bottom-color:var(--brand);color:var(--brand-dark);font-weight:700}.mobile-hidden{display:none!important}.page-head{flex:0 0 auto;padding-top:12px;padding-bottom:10px}.page-head h1{font-size:18px}.page-head>div>p{display:none}.skill-routing{margin-top:5px}.skill-picker{max-width:138px}.client-layout{display:block;flex:1}.conversation,.task-panel{height:100%}.task-panel{padding:16px 14px}.agent-live{margin:0 0 14px}.thinking-copy{max-height:none;overflow:visible}.bubble{max-width:92%}
}
</style>
